import { Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { ResultSection } from '@components/Assessments/Bha';
import AssessmentHeader from '@components/Assessments/Bha/Header';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';

import { ASSESSMENT_SECTIONS_MAP } from '@constants/assessment';

import { useAssessmentResult } from '@hooks/useAssessmentResult';

import { FontWeight } from '../../../theme/typography';

interface AssessmentResultDrilldownProps {
  /**
   * Forwarded to the header to enable the Share action. Omitted on the external
   * shared page so it renders read-only.
   */
  shareContext?: { assessment_id: string } | null;
}

function AssessmentResultDrilldown({
  shareContext = null,
}: AssessmentResultDrilldownProps) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const { loading } = useAssessmentResult();

  return (
    <ContentLoader loading={loading}>
      <Stack pb={4} spacing={2}>
        <AssessmentHeader shareContext={shareContext} />

        {Object.entries(ASSESSMENT_SECTIONS_MAP).map(
          ([header, { icon, metrics }]) => {
            return (
              <Stack key={`bha-header-${header}`} spacing={1}>
                <Stack alignItems="center" direction="row" spacing={1.5}>
                  <Icon
                    color="text.secondary"
                    name={icon}
                    size="sm"
                    type="fat"
                    style={{
                      border: `0.5px solid ${palette.grey['400']}`,
                      borderRadius: '8px',
                      marginRight: 1,
                      padding: 0.5,
                      width: 25,
                      height: 25,
                    }}
                  />
                  <Typography fontWeight={FontWeight.Light} variant="h5">
                    {formatMessage({ id: header })}
                  </Typography>
                </Stack>

                <Stack key={`assessment-${header}`} spacing={2}>
                  {metrics.map((type) => (
                    <ResultSection key={type} type={type} />
                  ))}
                </Stack>
              </Stack>
            );
          }
        )}
      </Stack>
    </ContentLoader>
  );
}

export default withErrorBoundary(AssessmentResultDrilldown, {
  FallbackComponent: ErrorBoundaryFallback,
});
